import type { Material, Object3D } from "three";
import type { PickingService } from "./pickingService";

/**
 * Whether a surface can hide what is behind it in the depth buffer. A faded or
 * hidden part, or anything drawn without depth writes, does not.
 *
 * Shared by the two placements that must stay in sight: Spark's standing spot
 * (`runtime/assets/sparkBehavior.ts`) and a comment Spark adds
 * (`runtime/annotations/annotationPlacement.ts`).
 */
export function isDepthOccludingMaterial(material: Material): boolean {
  return (
    material.visible &&
    material.depthTest &&
    material.depthWrite &&
    material.opacity > 0.01
  );
}

/**
 * A scene node that can hide a point behind it: a tagged scene entity (not the
 * floor, grid or other environment) whose material writes depth. A node with
 * no material is a proxy and counts as solid.
 */
export function isDepthOccludingSceneNode(picker: PickingService, node: Object3D): boolean {
  if (picker.resolveSnapshotId(node) === null) return false;
  const material = (node as Object3D & { material?: Material | Material[] }).material;
  if (!material) return true;
  return Array.isArray(material)
    ? material.some(isDepthOccludingMaterial)
    : isDepthOccludingMaterial(material);
}
